import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { DevMockRegistry } from '@shared/directives/dev-mock.directive';
import { DevBottomBarService } from './dev-bottom-bar.service';
import { environment } from '@env/environment';

const LS_POS = 'devTools.pos';

interface PanelState {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-dev-tools',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslatePipe],
  templateUrl: './dev-tools.component.html',
  styleUrls: ['./dev-tools.component.css']
})
export class DevToolsComponent implements AfterViewInit {
  @Output() closed = new EventEmitter<void>();
  @ViewChild('panel') panelRef!: ElementRef<HTMLElement>;

  private readonly registry = inject(DevMockRegistry);
  private readonly bottomBar = inject(DevBottomBarService);
  readonly mockVisible$ = this.registry.visible$;
  readonly networkVisible$ = this.bottomBar.networkVisible$;
  readonly consoleVisible$ = this.bottomBar.consoleVisible$;
  readonly networkFadeMs$ = this.bottomBar.fadeDurationNetworkMs$;
  readonly consoleFadeMs$ = this.bottomBar.fadeDurationConsoleMs$;
  readonly swaggerUrl = `${environment.baseApiUrl}/swagger-ui/index.html#/`;
  readonly quickLinks = environment.quickLinks;

  toggleMock(): void {
    this.registry.visible$.next(!this.registry.visible$.value);
  }

  toggleNetwork(): void {
    this.bottomBar.toggleNetwork();
  }

  toggleConsole(): void {
    this.bottomBar.toggleConsole();
  }

  stepNetworkFade(delta: number): void {
    const current = this.bottomBar.fadeDurationNetworkMs$.value;
    this.bottomBar.setFadeDurationNetworkMs(current + delta * 1000);
  }

  stepConsoleFade(delta: number): void {
    const current = this.bottomBar.fadeDurationConsoleMs$.value;
    this.bottomBar.setFadeDurationConsoleMs(current + delta * 1000);
  }

  ngAfterViewInit(): void {
    const saved = localStorage.getItem(LS_POS);
    if (!saved) return;
    try {
      const state: PanelState = JSON.parse(saved);
      const panel = this.panelRef.nativeElement;
      panel.style.top = `${state.top}px`;
      panel.style.left = `${state.left}px`;
      panel.style.width = `${state.width}px`;
      panel.style.height = `${state.height}px`;
      // Remove CSS-based `right` so left takes over
      panel.style.right = 'unset';
    } catch {
      // ignore malformed data
    }
  }

  resetPanel(): void {
    localStorage.removeItem(LS_POS);
    const panel = this.panelRef.nativeElement;
    panel.style.top = '70px';
    panel.style.left = '';
    panel.style.right = '16px';
    panel.style.width = '420px';
    panel.style.height = '240px';
  }

  private saveState(): void {
    const panel = this.panelRef.nativeElement;
    const state: PanelState = {
      top: panel.offsetTop,
      left: panel.offsetLeft,
      width: panel.offsetWidth,
      height: panel.offsetHeight,
    };
    localStorage.setItem(LS_POS, JSON.stringify(state));
  }

  startDrag(event: MouseEvent): void {
    const panel = this.panelRef.nativeElement;
    const startX = event.clientX - panel.offsetLeft;
    const startY = event.clientY - panel.offsetTop;

    const onMove = (e: MouseEvent) => {
      panel.style.right = 'unset';
      panel.style.left = `${e.clientX - startX}px`;
      panel.style.top = `${e.clientY - startY}px`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this.saveState();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  startResize(event: MouseEvent): void {
    event.stopPropagation();
    const panel = this.panelRef.nativeElement;
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = panel.offsetWidth;
    const startH = panel.offsetHeight;

    const onMove = (e: MouseEvent) => {
      panel.style.width = `${Math.max(200, startW + e.clientX - startX)}px`;
      panel.style.height = `${Math.max(150, startH + e.clientY - startY)}px`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this.saveState();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
}
